import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { Spinner, updateSpinner } from "@spectrumselect/mi-ui-react";
import { JPMAMLogo } from "./components/jpmamlogo/JPMAMLogo";
import { XmSdk } from "./xm/js-es6/xmsdk-es6.js"
import { CustomUiHandler } from "./auth/CustomUiHandler";
import { RootState } from "./redux/store";
import { AppState, clearFormState, revertToUserNameForm, revertToChangePasswordForm, updateStandardFormErrorMessage, updateCurrentState } from "./redux/AppStateSlice";
import { RememberDeviceForm } from "./features/rememberDeviceForm/RememberDeviceForm";
import { ChangePasswordForm } from "./features/ChangePassword/ChangePassword";
import { CollectPasswordForm } from "./features/collectPasswordForm/CollectPasswordForm";
import { CollectUsernameForm } from "./features/collectUsernameForm/CollectUsernameForm";
import { RegistrationInformationForm } from "./features/registrationInformationForm/RegistrationInformationForm";
import { RegistrationLinkExpiredForm } from "./features/registrationLinkExpiredForm/RegistrationLinkExpiredForm";
import { OTPInputForm } from "./features/OTPInputForm/OTPInputForm";
import { Journey } from "./redux";
import { ConfirmationInputForm } from "./features/confirmationInputForm/ConfirmationInputForm";
import { InformationResponse } from "./features/InformationResponse/InformationResponse";
import { SelectionUI } from "./features/selectionUI/SelectionUI";
import { ProcessJsonData } from "./features/ProcessJsonData/ProcessJsonData";
import { getLogger } from "./logConfig";
import { getInitiatingPolicyId } from "./auth/helpers";
import { SendOTPForm } from "./features/sendOTPForm/SendOTPForm";
import { DEFAULT_CAM_ERROR_MESSAGE, errorMessages } from "./auth/CustomFormSession";
import { local } from "./internationalization/Localization";
import { ErrorMessage } from "./components/errorMessage/ErrorMessage";
import UsernameForm from "./features/usernameForm/UsernameForm";
import Footer from "./layout/Footer/Footer";
import { TENANT, MAILTO, ERRORCODE, EMAIL_OTP_TEMPLATE, DEPLOYMENT_ID, DEFAULT_LOCALE, VERIFICATION_SUBJECT } from "./utils/constants";
import { getSafeHostUrl } from "./utils/urlUtils";
import "./styles/main.scss";



const endpoint = process.env.REACT_APP_LOCAL ? "https://nwas-sit.jpmorgan.com/awm" // For local development: Transmit server endpoint in SIT
  : "https://nwas-ent.jpmorgan.com/awm";  // Replaced with Transmit server endpoint from jenkins file
  // const miEndpoint = process.env.REACT_APP_LOCAL ? 'https://focus-dev.55ip.jpmchase.net/' // For local development: MI URL in UAT
//   : 'https://focus.55-ip.com';  // Replaced with MI UAT from jenkins file
// const backButtonURL = process.env.REACT_APP_LOCAL ? 'https://authe-ent-uat.jpmorgan.com/idp/startSLO.ping?TargetResource=' // For local development: UAT Ping URL to be hit on press of back button
//   : 'https://authe-ent.jpmorgan.com/idp/startSLO.ping?TargetResource=';  // Replaced with Back Button Ping URL from jenkins file


interface PayloadObject {
  iss: string;
  exp: number;
  jti: string;
  iat: number;
  extras: {
    realmparam?: string;
    realmParam?: string; // TODO: remove this when the PROD payload is updated
    aud: string;
    nonce: string;
    pwdOnly?: string;
    lob: string;
    prompt?: String;
    tracking_id: string;
    client_id: string;
    application: string;
    tname: string;
    host?: string;
    change_password?: string;
    username?: string;
    errorPage?: string;
    action?: string;
    sso_next?: string;
    msgid?: string;
    msgID?: string;  // TODO: remove this when the PROD payload is updated
    acr_values?: string; // TODO: remove this when the PROD payload is updated
  };
  realm: string;
  resumePath: string;
  baseCallbackUrl: string;
  userid: string | null;
  lob: string;
}

const urlParams = new URLSearchParams(window.location.search);
const paramToken = urlParams.get("params");
const payload: PayloadObject = paramToken ? JSON.parse(atob(paramToken.split(".")[1])) : undefined;
const appID = "104596_authe_app"; // Transmit application id
const messageId = payload?.extras?.msgid || payload?.extras?.msgID; // TODO: cleanup when the PROD payload is updated
window.addEventListener("pageshow", () => {
  if (!!payload?.resumePath && !!window.sessionStorage && window.sessionStorage.getItem('55ip-resumePath') === payload?.resumePath) {
    document.location.href = getSafeHostUrl(payload?.extras?.host || 'focus.55-ip.com');
  }
  window.sessionStorage.setItem("55ip-resumePath", payload?.resumePath);
});

export const sdk: com.ts.mobile.sdk.TransmitSDKXm = XmSdk(); //must be in root of file
sdk.setLogLevel(com.ts.mobile.sdk.LogLevel.Debug);
const connectionSettings = com.ts.mobile.sdk.SDKConnectionSettings.create(endpoint, appID, "", "");
sdk.setConnectionSettings(connectionSettings);

const logger = getLogger("AuthE App");

export interface AuthenticationErrorData {
  failure_data: {
    reason: {
      type: string;
      data: any;
    }
    source?: {
      method?: string;
    }
  }
}

export interface LoginAuthErrorData {
  additional_data: {
    errors: [{
      code: string;
      message: string;
    }]
  }
}

const App: React.FunctionComponent = () => {
  const dispatch = useDispatch();
  const appState = useSelector((state: RootState) => state.appState);
  const appStateRef = useRef(appState);
  const [successMsg, setSuccessMsg] = useState("");
  const [isActivation, setIsActivation] = useState(false);
  const [isInitialized, setIsInitialized] = useState<boolean>();
  const [tenant] = useState(payload?.extras?.tname || "focus");
  const [host] = useState(payload?.extras?.host || "focus-dev.55ip.jpmchase.net");
  const [showErr] = useState(payload?.extras?.errorPage || false);

  // useEffect(() => {
  //   const timeout = window.setTimeout(() => {
  //     document.location.href = miEndpoint;
  //   }, 29 * 60 * 1000)
  //   return () => {
  //     window.clearTimeout(timeout)
  //   }
  // }, [])


  useEffect(() => {
    const uiHandler = new CustomUiHandler(dispatch) as any as com.ts.mobile.sdk.UIHandler;
    sdk.setUiHandler(uiHandler);

    const asyncCalls: Promise<any>[] = [
      sdk.initialize()
    ]
    if (!process.env.REACT_APP_LOCAL) { //don't validate locally. Not authorized
      asyncCalls.push(sdk.invokeAnonymousPolicy("validateJWT", {
        jwt: paramToken,
        tracking_id: payload?.extras?.tracking_id
      }, {})) //validate JWT param token
    }

    Promise.all(asyncCalls)
      .then(() => {
        setIsInitialized(true)
        setSuccessMsg("")
      if (payload?.extras?.action && payload?.extras?.action === "internal") {
          invokeLoginJourney("wf", "");
        }
        if (messageId && messageId !== "none") {
          invokeUserRegistrationJourney();
        } else {
          if (payload?.extras?.action && payload?.extras?.action === "mod_cipher") {
            dispatch(revertToChangePasswordForm());
          } else {
            if (payload?.extras?.action && payload?.extras?.action === "link_cipher") {
              setIsActivation(true)
              invokeForgotPasswordJourney();
            } else {
              dispatch(revertToUserNameForm());
            }
          }
        }
      })
      .catch((e: any) => {
        logger.error("SDK initialization failed: ", e);
        setIsInitialized(false)
        updateSpinner({ show: false });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    appStateRef.current = appState;
  }, [appState]);

  //--- journey methods ---//
  const invokeLoginJourney = (username: string, password: string) => {
    const journeyId = Journey.LOGIN;
    const journeyParams = {
      aud: payload?.extras?.aud,
      acr_values: payload?.extras?.acr_values, // TODO: remove this when the PROD payload is updated
      tracking_id: payload?.extras?.tracking_id,
      jwt: paramToken,
      ids: username === "wf" ? "ida" : "",
      templateName: EMAIL_OTP_TEMPLATE,
      deploymentId: DEPLOYMENT_ID,
      locale: DEFAULT_LOCALE,
      subject: VERIFICATION_SUBJECT
    };
    const clientContext = { username: username, password: password };

    sdk.authenticate(username, journeyId, journeyParams, clientContext)
      .then((result: com.ts.mobile.sdk.AuthenticationResult) => {
        handleFormPost(username, result.getToken());
      })
      .catch((error: com.ts.mobile.sdk.AuthenticationError) => {
        const loginAuthErrorData = error.getData() as LoginAuthErrorData;
        const authenticationErrorData = error.getData() as AuthenticationErrorData;
        const errorCode = error.getErrorCode();
        logger.info(`ERROR: error occurred in invokeDefaultAuthJourney with error code: ${errorCode} and error data: ${JSON.stringify(loginAuthErrorData)}`);

        // invalid token
        if (authenticationErrorData?.failure_data?.reason?.data?.invalidAccessToken) {
          setIsInitialized(false);
        }
        //otp locked
        else if (authenticationErrorData?.failure_data?.reason?.data?.locked && authenticationErrorData?.failure_data?.source?.method === "otp") {
          handleErrorDefault(local.get("invokeAnonymousJourney.errors.otpLocked"));
        }
        // 7: ?
        else if (errorCode === ERRORCODE.SEVEN) {
          handleJourneyCancellation();
        }
        // 5: valid user with valid password expired
        else if (errorCode === ERRORCODE.FIVE) {
          handleErrorDefault(errorMessages.PWD_CORRECT_BUT_EXPIRED);
        }
        // 1: user with invalid userId or password
        else if (errorCode === ERRORCODE.ONE) {
          let errorKey = loginAuthErrorData?.additional_data?.errors[0]?.code;
          const errMessage = errorMessages[errorKey] ? errorMessages[errorKey] : loginAuthErrorData?.additional_data?.errors[0]?.message;
          handleErrorDefault(errMessage);
        } else {
          handleErrorDefault(appState.authenticationFailureMessage || undefined);
        }
      });
  };

  const onEnterUsernameAndPassword = (username: string, password: string) => {
    setSuccessMsg("");
    if (username === "" || password === "") {
      dispatch(updateStandardFormErrorMessage(errorMessages.INVALID_CREDENTIALS))
    } else {
      dispatch(clearFormState());
      invokeLoginJourney(username, password);
    }
  };

  const invokeUserRegistrationJourney = () => {
    const journeyParams = {
      msgid: messageId,
      aud: payload?.extras?.aud,
      tracking_id: payload?.extras?.tracking_id,
      jwt: paramToken,
      pwdOnly: payload?.extras?.pwdOnly
    };
    invokeAnonymousJourney(Journey.VERIFY_USER_REGISTRATION_EMAIL, journeyParams);
  };

  const invokeForgotPasswordJourney = (errorMessage?: string) => {
    const journeyParams = {
      aud: payload?.extras?.aud,
      tracking_id: payload?.extras?.tracking_id,
      jwt: paramToken,
      templateName: EMAIL_OTP_TEMPLATE,
      deploymentId: DEPLOYMENT_ID,
      locale: DEFAULT_LOCALE,
      subject: VERIFICATION_SUBJECT
    };
    invokeAnonymousJourney(Journey.FORGOT_PASSWORD, journeyParams, errorMessage);
  };
  const invokeChangePasswordJourney = (username: string, errorMessage?: string) => {
    if (username === "") {
      dispatch(updateStandardFormErrorMessage(errorMessages.INVALID_CREDENTIALS))
    } else {
      dispatch(clearFormState());
      const journeyParams = {
        aud: payload?.extras?.aud,
        tracking_id: payload?.extras?.tracking_id,
        username: username,
      };
      invokeAnonymousJourney(Journey.CHANGE_PASSWORD, journeyParams, errorMessage);
    }
  };

  const changePasswordInit = () => {
    dispatch(updateCurrentState({
      state: AppState.CHANGE_PASSWORD
    }));
  };
  const goToUsernameForm = () => {
    dispatch(updateCurrentState({
      state: AppState.USERNAME_PASSWORD
    }));
    setSuccessMsg("");
    setIsActivation(false);
  };

  const invokeAnonymousJourney = (journeyId: string,
    journeyParams: any,
    errorMessage?: string) => {
    const clientContext: any = {};
    if (errorMessage) clientContext.errorMessage = errorMessage;

    sdk.invokeAnonymousPolicy(journeyId, journeyParams, clientContext)
      .then((result: com.ts.mobile.sdk.AuthenticationResult) => {
        setSuccessMsg("");
        if (result.getToken() && result.getToken() !== "") {
          handleFormPost(appStateRef.current.redirectionUserId, result.getToken());
        } else {
          handleLogout();
          const msgTxt = isActivation ? "You have successfully set your password. Please log in now." : "You have successfully changed your password. Please log in now."
          handleJourneyCancellation();
          setSuccessMsg(msgTxt);
        }
        // handleFormPost(appStateRef.current.redirectionUserId, result.getToken());
      })
      .catch((error: com.ts.mobile.sdk.AuthenticationError) => {
        setSuccessMsg("");
        const errorCode = error.getErrorCode();
        const authenticationErrorData = error.getData() as AuthenticationErrorData;
        logger.info(`ERROR: error occurred in invokeAnonymousJourney with error code ${errorCode} and message: ${error.getMessage()}`);
        //User requested to reenter their username
        if (authenticationErrorData?.failure_data?.reason?.data?.reenter_user_id) {
          invokeAnonymousJourney(journeyId, journeyParams);
        }
        //invalid or inactive user
        else if (authenticationErrorData?.failure_data?.reason?.data?.invalidOrInactiveUser) {
          handleErrorDefault(errorMessages.ACCOUNT_DISABLED_AND_INVALID);
        }
        //otp locked
        else if (authenticationErrorData?.failure_data?.reason?.data?.locked && authenticationErrorData?.failure_data?.source?.method === "otp") {
          handleErrorDefault(local.get("invokeAnonymousJourney.errors.otpLocked"));
        }
        //7: ?
        else if (errorCode === ERRORCODE.SEVEN) {
          handleJourneyCancellation();
        }
        // 9: Session rejected
        else if (errorCode === ERRORCODE.NINE) {
          // Registration page rejected (user has already registered in another browser window)
          if (journeyId === Journey.VERIFY_USER_REGISTRATION_EMAIL) {
            handleErrorDefault("");
          } else if (authenticationErrorData?.failure_data?.reason?.data?.error) {
            handleErrorDefault(authenticationErrorData?.failure_data?.reason?.data?.error)
          } else handleErrorDefault();
        }
        // 11: Auth session Expired
        else if (errorCode === ERRORCODE.ELEVEN) {
          //otp session expired
          if (appStateRef.current.currentState === AppState.OTP_INPUT) {
            handleErrorDefault(local.get("invokeAnonymousJourney.errors.otpExpired"));
          } else handleErrorDefault("");
        } else handleErrorDefault()
      });
  };

  const handleErrorDefault = (errorMessage?: string) => {
    dispatch(revertToUserNameForm());
    dispatch(updateStandardFormErrorMessage(errorMessage || errorMessage === "" ? errorMessage : DEFAULT_CAM_ERROR_MESSAGE));
  };

  const handleJourneyCancellation = () => {
    setSuccessMsg("");
    setIsActivation(false);
    dispatch(revertToUserNameForm());
  };

  const handleLogout = () => {
    sdk.logout()
      .then(() => {
        logger.info("Transmit Session Ended");
      })
      .catch((err: any) => {
        logger.error("Error Ending Transmit Session: " + err.getMessage(), err);
      });
  }

  const getResumeURL = () => {
    const cburl = payload.baseCallbackUrl;
    if ((cburl.length - 1) === (cburl.lastIndexOf("/"))) {
      return cburl.substring(0, cburl.lastIndexOf("/")) + payload.resumePath;
    } else {
      return cburl + payload.resumePath;
    }
  };
  
  const handleFormPost = (username: string, tsToken: string) => {
    // POST back to Ping Federate. All parameters are required
    (document.getElementById("ping1") as HTMLFormElement).action = getResumeURL();
    (document.getElementById("ts1") as HTMLInputElement).value = tsToken;
    (document.getElementById("ts2") as HTMLInputElement).value = username;
    (document.getElementById("ts3") as HTMLInputElement).value = appID;
    (document.getElementById("ts4") as HTMLInputElement).name = payload.extras.realmparam || payload.extras.realmParam || "";  // TODO: cleanup when the PROD payload is updated
    (document.getElementById("ts4") as HTMLInputElement).value = payload.realm;
    (document.getElementById("ts5") as HTMLInputElement).value = "submit";
    handleLogout();
    (document.getElementById("ping1") as HTMLFormElement).submit();
  };

  const handleTenantUIScreen = () => {
    if (tenant === TENANT.FOCUS) {
      return (<div className="App_RightImage focus"><div className="login-over-img"></div></div>)
    }
    if (tenant === TENANT.BLACKROCK) {
      return (<div className="App_RightImage blackrock">
        <div className="login-over-img-blackrock">
        </div>
        <div>
          <div className="login-over-text">
            Delivering for clients, while streamlining your day-to-day.
            <div className="sub-text">
              We help you scale and simplify your
              practice by using models, all with no
              additional overlay fee when using a
              BlackRock model.<span className="ss-wrapper"><sup>1</sup></span>
            </div>
          </div>
        </div>

      </div>)
    }
    if (tenant === TENANT.JPMORGAN) {
      return (<div className="App_RightImage jpmorgan">
        <div className="login-over-img-jpmorgan">
        </div>
        <div className="login-over-img-jpm"></div></div>)
    }
    if (tenant === TENANT.WISDOMTREE) {
      return (<div className="App_RightImage wisdomtree">
        <div className="login-over-img-wisdomtree">
        </div>
        <div className="login-banner-text">
          Tax wisdom meets model portfolios
        </div></div>)
    }
    if (tenant === TENANT.RIVERFRONT) {
      return (<div className="App_RightImage riverfront">
        <div className='login-over-img-riverfront'></div>
        <div className="login100-more"></div>
        <div className="login-banner-text">
          Tax Efficient Investing
          <div className="sub-text">Custom &amp; Dynamic</div>
        </div>
      </div>)
    }
    if (tenant === TENANT.FIDELITY) {
      return (<div className="App_RightImage fidelity">
        <div className="login-over-img-fidelity">
        </div>
        <div>
          <div className="login-over-text">
            Leverage Tax Smart Technology to Better Serve Your Clients
          </div>
        </div>
      </div>)
    }
    if (tenant === TENANT.RAYMONDJAMES) {
      return (<div className="App_RightImage raymondjames">
        <div className="login-over-img-raymondjames" />
        <div className="login-over-img-raymondjames-logo-txt">Direct Index</div>
        <div>
          <div className="login-over-text">
            Leverage <br/> tax-smart technology to <br />better serve your clients
          </div>
        </div>
      </div>)
    }
  }
  //--- ui setup ---//
  const isUsernameForm = appState.currentState === AppState.USERNAME_PASSWORD;
  const pageForm = isUsernameForm ? (
    <UsernameForm
      onEnter={onEnterUsernameAndPassword}
      tenant={tenant}
      host={host}
      sso_next={payload?.extras?.sso_next}
      errorMessage={appState.currentErrorMessage}
      successMessage={successMsg}
      invokeForgotPasswordJourney={invokeForgotPasswordJourney}
      changePasswordInit={changePasswordInit}
      invokeLoginJourney={invokeLoginJourney}
    />
  ) : appState.currentState === AppState.REMEMBER_DEVICE ? (
    <RememberDeviceForm tenant={tenant} resolve={appState.standardFormProps?.currentResolve} />
  ) : appState.currentState === AppState.COLLECT_PASSWORD ? (
    <CollectPasswordForm
      tenant={tenant}
      resolve={appState.standardFormProps?.currentResolve}
      isActivation={payload?.extras?.action === "link_cipher" && isActivation}
      initiatingPolicyId={getInitiatingPolicyId(appState.standardFormProps?.actionContext) as Journey}
      errorMessage={appState.currentErrorMessage}
    />
  ) : appState.currentState === AppState.COLLECT_CURRENT_AND_NEW_PASSWORD ? (
    <CollectPasswordForm
      tenant={tenant}
      resolve={appState.standardFormProps?.currentResolve}
      initiatingPolicyId={getInitiatingPolicyId(appState.standardFormProps?.actionContext) as Journey}
      errorMessage={appState.currentErrorMessage}
    />
  ) : appState.currentState === AppState.COLLECT_USERNAME ? (
    <CollectUsernameForm
      tenant={tenant}
      username={payload?.extras?.action === "link_cipher" && isActivation ? payload?.extras?.username : undefined}
      isActivation={payload?.extras?.action === "link_cipher" && isActivation}
      resolve={appState.standardFormProps?.currentResolve}
      errorMessage={appState.standardFormProps?.clientContext?.errorMessage}
    />
  ) : appState.currentState === AppState.REGISTRATION_INFORMATION ? (
    <RegistrationInformationForm
      tenant={tenant}
      resolve={appState.standardFormProps?.currentResolve}
      formId={appState.standardFormProps?.currentFormId}
      email={appState.standardFormProps?.currentPayload?.email || ""}
    />
  ) : appState.currentState === AppState.CHANGE_PASSWORD ? (
    <ChangePasswordForm
      tenant={tenant}
      resolve={appState.standardFormProps?.currentResolve}
      errorMessage={appState.standardFormProps?.clientContext?.errorMessage}
      username={payload?.extras?.action === "mod_cipher" ? payload?.extras?.username : undefined}
      invokeChangePasswordJourney={invokeChangePasswordJourney}
      goToUsernameForm={goToUsernameForm}
    />
  )
    : appState.currentState === AppState.REGISTRATION_LINK_EXPIRED ? (
      <RegistrationLinkExpiredForm />
    ) : (appState.currentState === AppState.SEND_OTP && appState.sendOTPFormProps) ? (
      <SendOTPForm  {...appState.sendOTPFormProps} tenant={tenant} />
    ) : appState.currentState === AppState.OTP_INPUT && appState.otpInputFormProps ? (
      <OTPInputForm {...appState.otpInputFormProps} errorMessage={appState.currentErrorMessage} tenant={tenant} />
    ) : appState.currentState === AppState.CONFIRMATION_INPUT && appState.confirmationInputFormProps ? (
      <ConfirmationInputForm {...appState.confirmationInputFormProps} />
    ) : appState.currentState === AppState.INFORMATION_RESPONSE && appState.informationResponseProps ? (
      <InformationResponse {...appState.informationResponseProps} />
    ) : appState.currentState === AppState.SELECTION_UI && appState.selectionUIProps ? (
      <SelectionUI {...appState.selectionUIProps} />
    ) : appState.currentState === AppState.PROCESS_JSON && appState.processJsonDataProps ? (
      <ProcessJsonData {...appState.processJsonDataProps} />
    ) : <></>;

  return (
    <div className="App">
      <form id="ping1" action="https://focus.55-ip.com/" method="POST" className="hidden">
        <input id="ts1" type="hidden" name="ts_token" value="a" />
        <input id="ts2" type="hidden" name="ts_user" value="a" />
        <input id="ts3" type="hidden" name="ts_appid" value="a" />
        <input id="ts4" type="hidden" name="ts_lob" value="a" />
        <input id="ts5" type="hidden" name="ts_click" value="a" />
      </form>
      <main className="App_MainContainer">
        {handleTenantUIScreen()}
        <div className={classNames("App_MainContent", {
          "App_MainContent-withImage": ((payload?.extras?.action !== "internal"))
        })}>
          <div className="App_MainContentCol1">
            <JPMAMLogo className="App_JPLogo" />
            {(isInitialized && !showErr && (payload?.extras?.action !== "internal")) ? pageForm : (isInitialized === false || showErr)  ? (
              <div className="error-page">
              <h3 className="error-heading">Welcome to 55ip’s tax-smart investment strategy engine</h3>
              <ErrorMessage className="UsernameForm_ErrorMessage">
                <>
                  <p>
                    <span>
                      It looks like we're having trouble with that request. Please return to the login page and try again.
                    </span>
                  </p>
                  <span>
                      If you need additional help, reach out to <a href={MAILTO.link}>{MAILTO.text}</a>.
                  </span>
              </>
              </ErrorMessage>
              <div className={'App_formButtonGroup'}>
                <a href={getSafeHostUrl(host)} className={`UsernameForm_ResetPwdButton ${tenant}-text`}>Return to login </a>
              </div>
              </div>
            ) : <></>}
          </div>
        </div>
      </main>
      <Footer tenant={tenant} />
      <Spinner />
    </div>
  );
}

export default App;
